import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Zap, Plus, Loader2, Save, X, Pencil, Trash2, Clock, Repeat,
  Linkedin, Facebook, Instagram, GitBranch, Layers, Target,
  CheckCircle2, Calendar, TrendingUp, ArrowRight, Play, Pause
} from "lucide-react";

const PLATFORM_CONFIG = {
  linkedin: { label: "LinkedIn", icon: Linkedin, color: "#0a66c2" },
  facebook: { label: "Facebook", icon: Facebook, color: "#1877f2" },
  instagram: { label: "Instagram", icon: Instagram, color: "#e1306c" },
};

const DAYS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const DAY_LABELS = { lundi: "Lun", mardi: "Mar", mercredi: "Mer", jeudi: "Jeu", vendredi: "Ven", samedi: "Sam", dimanche: "Dim" };

const TONES = [
  { value: "professionnel", label: "Professionnel" },
  { value: "decontracte", label: "Decontracte" },
  { value: "inspirant", label: "Inspirant" },
  { value: "educatif", label: "Educatif" },
];

const CONDITIONAL_TRIGGERS = [
  { value: "high_performance", label: "Post performant" },
  { value: "low_engagement", label: "Faible engagement" },
  { value: "trending_topic", label: "Sujet tendance" },
];

const CONDITIONAL_ACTIONS = [
  { value: "repost_other_platform", label: "Reposter sur autre plateforme" },
  { value: "generate_similar", label: "Generer un post similaire" },
  { value: "boost_content", label: "Booster le contenu (variante)" },
];

function Toggle({ value, onChange }) {
  return (
    <button onClick={() => onChange(!value)}
      className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${value ? "bg-[#10b981]" : "bg-slate-600"}`}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${value ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

export function SocialAutomations({ api, onPostsCreated }) {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editAuto, setEditAuto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(getDefaultForm());

  // Pipeline
  const [showPipeline, setShowPipeline] = useState(false);
  const [pipelineForm, setPipelineForm] = useState({
    batch_size: 7, platforms: ["linkedin"], tone: "professionnel",
    prompt: "", topics: [], start_date: "",
  });
  const [pipelineTopicInput, setPipelineTopicInput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [pipelineResult, setPipelineResult] = useState(null);

  function getDefaultForm() {
    return {
      name: "", type: "recurring", enabled: true,
      recurring: { frequency: "weekly", days: ["lundi"], time: "09:00", platforms: ["linkedin"], tone: "professionnel", prompt: "", topics_rotation: [] },
      pipeline: { batch_size: 7, advance_days: 7, platforms: ["linkedin"], tone: "professionnel", prompt: "", auto_validate_after_hours: 0 },
      conditional: { trigger: "high_performance", condition_metric: "engagement", condition_threshold: 80, action: "repost_other_platform", action_platform: "facebook", action_delay_hours: 24 },
    };
  }

  useEffect(() => { loadAutomations(); }, []);

  const loadAutomations = async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketing/social-automations");
      setAutomations(r.data || []);
    } catch { setAutomations([]); }
    setLoading(false);
  };

  const openNew = () => { setEditAuto(null); setForm(getDefaultForm()); setShowDialog(true); };
  const openEdit = (a) => {
    setEditAuto(a);
    setForm({
      name: a.name, type: a.type, enabled: a.enabled !== false,
      recurring: { ...getDefaultForm().recurring, ...a.recurring },
      pipeline: { ...getDefaultForm().pipeline, ...a.pipeline },
      conditional: { ...getDefaultForm().conditional, ...a.conditional },
    });
    setShowDialog(true);
  };

  const saveAuto = async () => {
    setSaving(true);
    try {
      if (editAuto) {
        await api.put(`/marketing/social-automations/${editAuto.automation_id}`, form);
      } else {
        await api.post("/marketing/social-automations", form);
      }
      setShowDialog(false);
      loadAutomations();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const deleteAuto = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/marketing/social-automations/${id}`);
    loadAutomations();
  };

  const toggleAuto = async (a) => {
    await api.put(`/marketing/social-automations/${a.automation_id}`, { enabled: !a.enabled });
    loadAutomations();
  };

  // Pipeline
  const togglePipelinePlatform = (p) => {
    setPipelineForm(f => ({
      ...f,
      platforms: f.platforms.includes(p) ? f.platforms.filter(x => x !== p) : [...f.platforms, p],
    }));
  };

  const addPipelineTopic = () => {
    if (pipelineTopicInput.trim()) {
      setPipelineForm(f => ({ ...f, topics: [...f.topics, pipelineTopicInput.trim()] }));
      setPipelineTopicInput("");
    }
  };

  const generatePipeline = async () => {
    setGenerating(true);
    setPipelineResult(null);
    try {
      const r = await api.post("/marketing/pipeline/generate", pipelineForm);
      setPipelineResult(r.data);
      if (r.data.posts_created > 0 && onPostsCreated) onPostsCreated();
    } catch (e) {
      setPipelineResult({ error: e.response?.data?.detail || "Erreur de generation" });
    }
    setGenerating(false);
  };

  // Form helpers
  const toggleFormDay = (day) => {
    setForm(f => ({
      ...f,
      recurring: {
        ...f.recurring,
        days: f.recurring.days.includes(day) ? f.recurring.days.filter(d => d !== day) : [...f.recurring.days, day],
      },
    }));
  };

  const toggleFormPlatform = (p, section) => {
    setForm(f => ({
      ...f,
      [section]: {
        ...f[section],
        platforms: f[section].platforms.includes(p) ? f[section].platforms.filter(x => x !== p) : [...f[section].platforms, p],
      },
    }));
  };

  const TYPE_CONFIG = {
    recurring: { label: "Recurrent", icon: Repeat, color: "#10b981", desc: "Publie automatiquement selon un calendrier" },
    pipeline: { label: "Pipeline", icon: Layers, color: "#3b82f6", desc: "Genere un lot de posts en avance" },
    conditional: { label: "Conditionnel", icon: GitBranch, color: "#f59e0b", desc: "Reagit aux performances des posts" },
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#10b981]" /></div>;

  return (
    <div data-testid="social-automations" className="space-y-5">
      {/* Header actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-400" /> Automations Reseaux Sociaux</h3>
          <p className="text-[10px] text-slate-500">Recurrence, pipeline de contenu, regles conditionnelles</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowPipeline(true)} size="sm" className="bg-[#3b82f6]/10 text-[#3b82f6] hover:bg-[#3b82f6]/20 text-xs" data-testid="open-pipeline-btn">
            <Layers className="w-3 h-3 mr-1" /> Pipeline
          </Button>
          <Button onClick={openNew} size="sm" className="bg-[#10b981] text-white text-xs" data-testid="add-social-auto-btn">
            <Plus className="w-3 h-3 mr-1" /> Automation
          </Button>
        </div>
      </div>

      {/* Automations list */}
      {automations.length === 0 ? (
        <div className="text-center py-12 bg-[#14101c] border border-white/5 rounded-xl">
          <Zap className="w-10 h-10 mx-auto mb-3 text-slate-600" />
          <p className="text-sm text-slate-500">Aucune automation configuree</p>
          <p className="text-[10px] text-slate-600 mt-1">Creez des automations pour publier du contenu automatiquement</p>
        </div>
      ) : (
        <div className="space-y-2">
          {automations.map(a => {
            const tc = TYPE_CONFIG[a.type] || TYPE_CONFIG.recurring;
            const TIcon = tc.icon;
            return (
              <div key={a.automation_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors" data-testid={`sauto-${a.automation_id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${tc.color}15` }}>
                      <TIcon className="w-3.5 h-3.5" style={{ color: tc.color }} />
                    </div>
                    <span className="text-sm font-bold">{a.name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${tc.color}15`, color: tc.color }}>{tc.label}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${a.enabled ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                      {a.enabled ? "Actif" : "Inactif"}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => toggleAuto(a)} className="p-1.5 hover:bg-white/5 rounded">
                      {a.enabled ? <Pause className="w-3 h-3 text-amber-400" /> : <Play className="w-3 h-3 text-green-400" />}
                    </button>
                    <button onClick={() => openEdit(a)} className="p-1.5 hover:bg-white/5 rounded"><Pencil className="w-3 h-3 text-slate-400" /></button>
                    <button onClick={() => deleteAuto(a.automation_id)} className="p-1.5 hover:bg-red-500/10 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 flex flex-wrap gap-3">
                  {a.type === "recurring" && (
                    <>
                      <span>{a.recurring?.frequency === "daily" ? "Quotidien" : a.recurring?.frequency === "weekly" ? "Hebdo" : "Mensuel"}</span>
                      <span>{(a.recurring?.days || []).map(d => DAY_LABELS[d]).join(", ")}</span>
                      <span>{a.recurring?.time || "09:00"}</span>
                      <span>{(a.recurring?.platforms || []).join(", ")}</span>
                    </>
                  )}
                  {a.type === "pipeline" && (
                    <>
                      <span>{a.pipeline?.batch_size || 7} posts / lot</span>
                      <span>J+{a.pipeline?.advance_days || 7}</span>
                      <span>{(a.pipeline?.platforms || []).join(", ")}</span>
                    </>
                  )}
                  {a.type === "conditional" && (
                    <>
                      <span>Si : {CONDITIONAL_TRIGGERS.find(t => t.value === a.conditional?.trigger)?.label}</span>
                      <span><ArrowRight className="w-3 h-3 inline" /> {CONDITIONAL_ACTIONS.find(t => t.value === a.conditional?.action)?.label}</span>
                    </>
                  )}
                  {a.stats?.posts_created > 0 && <span className="text-[#10b981]">{a.stats.posts_created} posts crees</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Automation Dialog */}
      <Dialog open={showDialog} onOpenChange={(v) => { setShowDialog(v); if (!v) setEditAuto(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editAuto ? "Modifier l'automation" : "Nouvelle automation"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Nom de l'automation *" className="bg-[#0b0811] border-white/10 text-white" data-testid="sauto-name" />

            {/* Type selector */}
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(TYPE_CONFIG).map(([k, cfg]) => {
                const TIcon = cfg.icon;
                return (
                  <button key={k} onClick={() => setForm(f => ({ ...f, type: k }))}
                    className={`p-3 rounded-lg text-center transition-all ${form.type === k ? "border-2" : "bg-[#0b0811] border border-white/5"}`}
                    style={form.type === k ? { borderColor: cfg.color, background: `${cfg.color}10` } : {}}
                    data-testid={`sauto-type-${k}`}>
                    <TIcon className="w-5 h-5 mx-auto mb-1" style={{ color: form.type === k ? cfg.color : "#64748b" }} />
                    <div className="text-[10px] font-bold" style={{ color: form.type === k ? cfg.color : "#94a3b8" }}>{cfg.label}</div>
                    <div className="text-[8px] text-slate-600 mt-0.5">{cfg.desc}</div>
                  </button>
                );
              })}
            </div>

            {/* Recurring config */}
            {form.type === "recurring" && (
              <div className="space-y-2 p-3 bg-[#0b0811] border border-white/5 rounded-lg">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase mb-1 block">Frequence</label>
                  <div className="flex gap-2">
                    {["daily", "weekly", "monthly"].map(f => (
                      <button key={f} onClick={() => setForm(fm => ({ ...fm, recurring: { ...fm.recurring, frequency: f } }))}
                        className={`flex-1 py-1.5 rounded text-[11px] font-medium ${form.recurring.frequency === f ? "bg-[#10b981]/15 text-[#10b981] border border-[#10b981]/30" : "bg-[#14101c] text-slate-500 border border-white/5"}`}>
                        {f === "daily" ? "Quotidien" : f === "weekly" ? "Hebdo" : "Mensuel"}
                      </button>
                    ))}
                  </div>
                </div>
                {form.recurring.frequency !== "daily" && (
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase mb-1 block">Jours</label>
                    <div className="flex gap-1">
                      {DAYS.map(d => (
                        <button key={d} onClick={() => toggleFormDay(d)}
                          className={`flex-1 py-1.5 rounded text-[10px] font-medium ${form.recurring.days.includes(d) ? "bg-[#10b981] text-white" : "bg-[#14101c] text-slate-500 border border-white/5"}`}>
                          {DAY_LABELS[d]}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <Input type="time" value={form.recurring.time} onChange={e => setForm(f => ({ ...f, recurring: { ...f.recurring, time: e.target.value } }))}
                  className="bg-[#14101c] border-white/10 text-white text-xs" />
                <div>
                  <label className="text-[10px] text-slate-500 uppercase mb-1 block">Plateformes</label>
                  <div className="flex gap-2">
                    {Object.entries(PLATFORM_CONFIG).map(([k, cfg]) => {
                      const Icon = cfg.icon;
                      return (
                        <button key={k} onClick={() => toggleFormPlatform(k, "recurring")}
                          className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded text-[11px] font-medium ${form.recurring.platforms.includes(k) ? "text-white" : "bg-[#14101c] text-slate-500 border border-white/5"}`}
                          style={form.recurring.platforms.includes(k) ? { background: cfg.color } : {}}>
                          <Icon className="w-3 h-3" /> {cfg.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <textarea value={form.recurring.prompt} onChange={e => setForm(f => ({ ...f, recurring: { ...f.recurring, prompt: e.target.value } }))}
                  placeholder="Instructions pour la generation (themes, ton, style...)"
                  className="w-full bg-[#14101c] border border-white/10 text-white rounded px-2 py-1.5 text-xs h-16 resize-none" />
              </div>
            )}

            {/* Pipeline config */}
            {form.type === "pipeline" && (
              <div className="space-y-2 p-3 bg-[#0b0811] border border-white/5 rounded-lg">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-[10px] text-slate-500 block mb-1">Posts par lot</label>
                    <Input type="number" min={1} max={30} value={form.pipeline.batch_size}
                      onChange={e => setForm(f => ({ ...f, pipeline: { ...f.pipeline, batch_size: parseInt(e.target.value) || 7 } }))}
                      className="bg-[#14101c] border-white/10 text-white text-xs" />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-slate-500 block mb-1">Jours d'avance</label>
                    <Input type="number" min={1} max={30} value={form.pipeline.advance_days}
                      onChange={e => setForm(f => ({ ...f, pipeline: { ...f.pipeline, advance_days: parseInt(e.target.value) || 7 } }))}
                      className="bg-[#14101c] border-white/10 text-white text-xs" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase mb-1 block">Plateformes</label>
                  <div className="flex gap-2">
                    {Object.entries(PLATFORM_CONFIG).map(([k, cfg]) => {
                      const Icon = cfg.icon;
                      return (
                        <button key={k} onClick={() => toggleFormPlatform(k, "pipeline")}
                          className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded text-[11px] font-medium ${form.pipeline.platforms.includes(k) ? "text-white" : "bg-[#14101c] text-slate-500 border border-white/5"}`}
                          style={form.pipeline.platforms.includes(k) ? { background: cfg.color } : {}}>
                          <Icon className="w-3 h-3" /> {cfg.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <textarea value={form.pipeline.prompt} onChange={e => setForm(f => ({ ...f, pipeline: { ...f.pipeline, prompt: e.target.value } }))}
                  placeholder="Instructions pour la generation batch..."
                  className="w-full bg-[#14101c] border border-white/10 text-white rounded px-2 py-1.5 text-xs h-16 resize-none" />
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Auto-validation apres (heures, 0=desactive)</label>
                  <select value={form.pipeline.auto_validate_after_hours}
                    onChange={e => setForm(f => ({ ...f, pipeline: { ...f.pipeline, auto_validate_after_hours: parseInt(e.target.value) } }))}
                    className="w-full bg-[#14101c] border border-white/10 text-white rounded px-2 py-1.5 text-xs">
                    {[0, 6, 12, 24, 48].map(h => <option key={h} value={h}>{h === 0 ? "Desactive" : `${h}h`}</option>)}
                  </select>
                </div>
              </div>
            )}

            {/* Conditional config */}
            {form.type === "conditional" && (
              <div className="space-y-2 p-3 bg-[#0b0811] border border-white/5 rounded-lg">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase mb-1 block">Condition (si...)</label>
                  <select value={form.conditional.trigger}
                    onChange={e => setForm(f => ({ ...f, conditional: { ...f.conditional, trigger: e.target.value } }))}
                    className="w-full bg-[#14101c] border border-white/10 text-white rounded px-2 py-1.5 text-xs">
                    {CONDITIONAL_TRIGGERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-[10px] text-slate-500 block mb-1">Metrique</label>
                    <select value={form.conditional.condition_metric}
                      onChange={e => setForm(f => ({ ...f, conditional: { ...f.conditional, condition_metric: e.target.value } }))}
                      className="w-full bg-[#14101c] border border-white/10 text-white rounded px-2 py-1.5 text-xs">
                      <option value="engagement">Engagement</option>
                      <option value="reach">Portee</option>
                      <option value="clicks">Clics</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-slate-500 block mb-1">Seuil (%)</label>
                    <Input type="number" min={0} max={100} value={form.conditional.condition_threshold}
                      onChange={e => setForm(f => ({ ...f, conditional: { ...f.conditional, condition_threshold: parseInt(e.target.value) || 0 } }))}
                      className="bg-[#14101c] border-white/10 text-white text-xs" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase mb-1 block flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Alors...</label>
                  <select value={form.conditional.action}
                    onChange={e => setForm(f => ({ ...f, conditional: { ...f.conditional, action: e.target.value } }))}
                    className="w-full bg-[#14101c] border border-white/10 text-white rounded px-2 py-1.5 text-xs">
                    {CONDITIONAL_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </div>
                {form.conditional.action === "repost_other_platform" && (
                  <div className="flex gap-2">
                    {Object.entries(PLATFORM_CONFIG).map(([k, cfg]) => {
                      const Icon = cfg.icon;
                      return (
                        <button key={k} onClick={() => setForm(f => ({ ...f, conditional: { ...f.conditional, action_platform: k } }))}
                          className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded text-[11px] font-medium ${form.conditional.action_platform === k ? "text-white" : "bg-[#14101c] text-slate-500 border border-white/5"}`}
                          style={form.conditional.action_platform === k ? { background: cfg.color } : {}}>
                          <Icon className="w-3 h-3" /> {cfg.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Delai avant action (heures)</label>
                  <Input type="number" min={0} value={form.conditional.action_delay_hours}
                    onChange={e => setForm(f => ({ ...f, conditional: { ...f.conditional, action_delay_hours: parseInt(e.target.value) || 0 } }))}
                    className="bg-[#14101c] border-white/10 text-white text-xs" />
                </div>
              </div>
            )}

            {/* Enabled */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-300">Activer immediatement</span>
              <Toggle value={form.enabled} onChange={v => setForm(f => ({ ...f, enabled: v }))} />
            </div>

            <Button onClick={saveAuto} disabled={saving || !form.name} className="w-full bg-[#10b981] text-white font-bold" data-testid="save-sauto-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editAuto ? "Modifier" : "Creer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pipeline Dialog */}
      <Dialog open={showPipeline} onOpenChange={setShowPipeline}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Pipeline de contenu</DialogTitle></DialogHeader>
          <p className="text-[10px] text-slate-500 -mt-1">Generez un lot de posts planifies en avance</p>
          <div className="space-y-3 mt-2">
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-[10px] text-slate-500 block mb-1">Nombre de posts</label>
                <Input type="number" min={1} max={30} value={pipelineForm.batch_size}
                  onChange={e => setPipelineForm(f => ({ ...f, batch_size: parseInt(e.target.value) || 7 }))}
                  className="bg-[#0b0811] border-white/10 text-white text-xs" data-testid="pipeline-batch-size" />
              </div>
              <div className="flex-1">
                <label className="text-[10px] text-slate-500 block mb-1">Date de debut</label>
                <Input type="date" value={pipelineForm.start_date}
                  onChange={e => setPipelineForm(f => ({ ...f, start_date: e.target.value }))}
                  className="bg-[#0b0811] border-white/10 text-white text-xs" />
              </div>
            </div>

            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1 block">Plateformes</label>
              <div className="flex gap-2">
                {Object.entries(PLATFORM_CONFIG).map(([k, cfg]) => {
                  const Icon = cfg.icon;
                  return (
                    <button key={k} onClick={() => togglePipelinePlatform(k)}
                      className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-medium ${pipelineForm.platforms.includes(k) ? "text-white" : "bg-[#0b0811] text-slate-500 border border-white/5"}`}
                      style={pipelineForm.platforms.includes(k) ? { background: cfg.color } : {}}>
                      <Icon className="w-3.5 h-3.5" /> {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1 block">Ton</label>
              <div className="flex gap-2">
                {TONES.map(t => (
                  <button key={t.value} onClick={() => setPipelineForm(f => ({ ...f, tone: t.value }))}
                    className={`flex-1 py-1.5 rounded text-[11px] font-medium ${pipelineForm.tone === t.value ? "bg-[#10b981]/15 text-[#10b981] border border-[#10b981]/30" : "bg-[#0b0811] text-slate-500 border border-white/5"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <textarea value={pipelineForm.prompt} onChange={e => setPipelineForm(f => ({ ...f, prompt: e.target.value }))}
              placeholder="Instructions : themes, style, objectifs..."
              className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-xs h-20 resize-none" data-testid="pipeline-prompt" />

            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1 block">Themes (optionnel)</label>
              <div className="flex flex-wrap gap-1 mb-1.5">
                {pipelineForm.topics.map((t, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[#10b981]/10 text-[#10b981] flex items-center gap-1">
                    {t} <button onClick={() => setPipelineForm(f => ({ ...f, topics: f.topics.filter((_, j) => j !== i) }))}><X className="w-2.5 h-2.5" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1">
                <Input value={pipelineTopicInput} onChange={e => setPipelineTopicInput(e.target.value)}
                  placeholder="Ajouter un theme" className="bg-[#0b0811] border-white/10 text-white text-xs flex-1"
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addPipelineTopic())} />
                <Button onClick={addPipelineTopic} size="sm" variant="outline" className="border-white/10 text-slate-400 text-xs"><Plus className="w-3 h-3" /></Button>
              </div>
            </div>

            <Button onClick={generatePipeline} disabled={generating || (!pipelineForm.prompt && pipelineForm.topics.length === 0)}
              className="w-full bg-[#10b981] text-white font-bold" data-testid="generate-pipeline-btn">
              {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generation en cours...</> : <><Layers className="w-4 h-4 mr-2" /> Generer {pipelineForm.batch_size} posts</>}
            </Button>

            {pipelineResult && (
              <div className={`p-3 rounded-lg border ${pipelineResult.error ? "bg-red-500/5 border-red-500/20" : "bg-[#10b981]/5 border-[#10b981]/20"}`} data-testid="pipeline-result">
                {pipelineResult.error ? (
                  <p className="text-xs text-red-400">{pipelineResult.error}</p>
                ) : (
                  <div>
                    <p className="text-xs text-[#10b981] font-bold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {pipelineResult.posts_created} posts crees et planifies !</p>
                    <div className="mt-2 space-y-1">
                      {(pipelineResult.posts || []).map((p, i) => (
                        <div key={i} className="text-[10px] text-slate-400 flex items-center gap-2">
                          <span className="text-[#10b981]">{p.platform}</span>
                          <span>{new Date(p.scheduled_at).toLocaleDateString("fr-FR")} {p.scheduled_at.split("T")[1]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
